import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, Skeleton, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TextField, Tooltip, IconButton, Typography, Paper,
  alpha, useTheme, FormControlLabel,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { getMasters, createMaster, updateMaster, deleteMaster } from '../../services/platform/masterService';

interface VehicleType {
  _id: string;
  code: string;
  name: string;
  isActive?: boolean;
  sortOrder?: number;
  metadata?: {
    capacity?: number;
    serviceType?: string;
    imageUrl?: string;
  };
}

const FALLBACK_VEHICLES = [
  { name: 'Auto', capacity: 3, serviceType: 'LOCAL' },
  { name: 'Sedan', capacity: 4, serviceType: 'ALL' },
  { name: 'SUV', capacity: 6, serviceType: 'ALL' },
  { name: 'Premium', capacity: 4, serviceType: 'OUTSTATION' },
  { name: 'Bike', capacity: 1, serviceType: 'LOCAL' },
];

const SERVICE_TYPES = ['LOCAL', 'RENTAL', 'OUTSTATION', 'ALL'];

const EMPTY_FORM = {
  name: '',
  code: '',
  capacity: 4,
  serviceType: 'ALL',
  sortOrder: 0,
  isActive: true,
};

export default function VehicleTypesPage() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  const { data: result, isLoading } = useQuery({
    queryKey: ['vehicle-types-admin'],
    queryFn: () => getMasters('vehicle_types', { limit: 200, page: 1 }),
  });

  const rows: VehicleType[] = result?.data || [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['vehicle-types-admin'] });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        code: form.code || form.name.toUpperCase().replace(/\s+/g, '_'),
        name: form.name,
        isActive: form.isActive,
        sortOrder: form.sortOrder,
        metadata: {
          capacity: form.capacity,
          serviceType: form.serviceType,
        },
      };
      if (editingId) return updateMaster(editingId, payload);
      return createMaster('vehicle_types', payload);
    },
    onSuccess: () => { invalidate(); handleClose(); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteMaster(id),
    onSuccess: invalidate,
  });

  const handleOpen = (row?: VehicleType) => {
    if (row) {
      setEditingId(row._id);
      setForm({
        name: row.name,
        code: row.code,
        capacity: row.metadata?.capacity || 4,
        serviceType: row.metadata?.serviceType || 'ALL',
        sortOrder: row.sortOrder || 0,
        isActive: row.isActive !== false,
      });
    } else {
      setEditingId(null);
      setForm({ ...EMPTY_FORM });
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
  };

  const pagedRows = rows.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <PageShell>
      <PageHeader
        eyebrow="Transport"
        title="Vehicle Types"
        subtitle="Manage vehicle categories — capacity, service type, and availability."
        gradient
        actions={
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
            Add Vehicle Type
          </Button>
        }
      />

      {isLoading ? (
        <Stack spacing={1}>{[1, 2, 3].map((i) => <Skeleton key={i} height={52} />)}</Stack>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          {!rows.length && (
            <Box sx={{ p: 3, bgcolor: alpha(theme.palette.info.main, 0.05), borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                No vehicle types found. Showing default fallback types below for reference.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {FALLBACK_VEHICLES.map((v) => (
                  <Chip key={v.name} size="small" label={`${v.name} (${v.capacity} seats · ${v.serviceType})`} variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 56 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Vehicle Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Capacity</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Service Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Sort</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 88 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((row, idx) => (
                  <TableRow key={row._id} hover sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}>
                    <TableCell>{page * pageSize + idx + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{row.code}</Typography>
                    </TableCell>
                    <TableCell>{row.metadata?.capacity ?? '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.metadata?.serviceType || '—'} variant="outlined" />
                    </TableCell>
                    <TableCell>{row.sortOrder ?? 0}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.isActive !== false ? 'Active' : 'Inactive'} color={row.isActive !== false ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpen(row)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => {
                          if (window.confirm(`Delete "${row.name}"?`)) deleteMutation.mutate(row._id);
                        }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!rows.length && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">
                        No vehicle types yet. Click "Add Vehicle Type" to create the first one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {rows.length > 0 && (
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          )}
        </Paper>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Vehicle Type' : 'Add Vehicle Type'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Vehicle Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Code (auto-generated if blank)"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              fullWidth
              size="small"
              helperText="e.g. SEDAN, SUV, BIKE"
            />
            <TextField
              label="Capacity (seats)"
              type="text"
              value={form.capacity}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw !== '' && /[^0-9]/.test(raw)) return;
                if (raw !== '' && Number(raw) < 1) return;
                setForm((f) => ({ ...f, capacity: raw === '' ? 1 : Number(raw) }));
              }}
              fullWidth
              size="small"
              inputProps={{ min: 1, max: 50, inputMode: 'numeric' }}
              helperText="Whole numbers from 1 to 50"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Service Type</InputLabel>
              <Select
                value={form.serviceType}
                label="Service Type"
                onChange={(e) => setForm((f) => ({ ...f, serviceType: e.target.value }))}
              >
                {SERVICE_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Sort Order"
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm((f) => ({ ...f, sortOrder: parseInt(e.target.value) || 0 }))}
              fullWidth
              size="small"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form.name || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {saveMutation.isPending ? 'Saving…' : editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
